import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Pencil } from 'lucide-react';
import { Movimiento } from '../lib/supabase';
import { movimientoService } from '../services/movimientoService';
import { miembroService } from '../services/miembroService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { ComprobantesDeMovimiento } from './comprobantes/ComprobantesDeMovimiento';
import { AdjuntarComprobanteModal } from './comprobantes/AdjuntarComprobanteModal';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import type { EntidadDinero, Respaldo } from '../types/comprobante';

interface EditarAporteModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly movimiento: Movimiento;
  /** 'nested' cuando se abre sobre otra ventana ya abierta (ej. el modal de aportes del miembro). */
  readonly level?: 'base' | 'nested';
}

type CategoriaAporte = 'aporte_mensual' | 'cuota_iglesia';

interface SelectorCategoriaAporteProps {
  readonly categoria: CategoriaAporte;
  readonly onChange: (categoria: CategoriaAporte) => void;
}

const SelectorCategoriaAporte: React.FC<SelectorCategoriaAporteProps> = ({ categoria, onChange }) => (
  <fieldset>
    <legend className="block text-sm font-semibold text-slate-700 mb-3">
      Categoría de Aporte *
    </legend>
    <div className="grid grid-cols-2 gap-4">
      <button
        type="button"
        onClick={() => { onChange('aporte_mensual'); }}
        className={`
          p-4 rounded-xl border-2 transition-all text-left
          ${categoria === 'aporte_mensual'
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-200 hover:border-slate-300'
          }
        `}
      >
        <div className="font-semibold text-slate-800">Aporte Lumisial</div>
      </button>
      <button
        type="button"
        onClick={() => { onChange('cuota_iglesia'); }}
        className={`
          p-4 rounded-xl border-2 transition-all text-left
          ${categoria === 'cuota_iglesia'
            ? 'border-amber-500 bg-amber-50'
            : 'border-slate-200 hover:border-slate-300'
          }
        `}
      >
        <div className="font-semibold text-slate-800">Cuota Iglesia</div>
      </button>
    </div>
  </fieldset>
);

interface DatosDelAportanteProps {
  readonly esModoMiembro: boolean;
  readonly nombreMiembro: string;
  readonly nombreExterno: string;
  readonly onNombreExternoChange: (nombre: string) => void;
  readonly categoria: CategoriaAporte;
  readonly onCategoriaChange: (categoria: CategoriaAporte) => void;
}

/** Un aporte de miembro muestra su nombre (sin editar) y la categoría; uno externo, el nombre editable. */
const DatosDelAportante: React.FC<DatosDelAportanteProps> = ({
  esModoMiembro, nombreMiembro, nombreExterno, onNombreExternoChange, categoria, onCategoriaChange,
}) => {
  if (!esModoMiembro) {
    return (
      <TextInput
        label="Nombre del Aportante"
        value={nombreExterno}
        onChange={onNombreExternoChange}
        placeholder="Nombre completo de la persona externa"
        required
      />
    );
  }
  return (
    <>
      <TextInput
        label="Miembro"
        value={nombreMiembro || 'Cargando...'}
        onChange={() => undefined}
        disabled
      />
      <SelectorCategoriaAporte categoria={categoria} onChange={onCategoriaChange} />
    </>
  );
};

interface ComprobanteDelAporteProps {
  readonly conComprobantes: { readonly movimiento: Movimiento; readonly ahora: Date } | null;
  readonly puedeAdjuntar: boolean;
  readonly onAdjuntar: (respaldo: Respaldo, modo: 'adjuntar' | 'reemplazar') => void;
}

/** El comprobante se ve, se adjunta o se reemplaza; aquí no se quita ni se exige. */
const ComprobanteDelAporte: React.FC<ComprobanteDelAporteProps> = ({ conComprobantes, puedeAdjuntar, onAdjuntar }) => {
  if (!conComprobantes) return null;
  return (
    <div>
      <p className="block text-sm font-medium text-slate-600 mb-1.5">Comprobante</p>
      <ComprobantesDeMovimiento
        movimiento={conComprobantes.movimiento}
        ahora={conComprobantes.ahora}
        onAdjuntar={puedeAdjuntar ? (_, respaldo, modo) => { onAdjuntar(respaldo, modo); } : undefined}
      />
    </div>
  );
};

interface AdjuntarComprobanteDelAporteProps {
  readonly movimiento: Movimiento;
  readonly entidad: EntidadDinero;
  readonly paraAdjuntar: { readonly respaldo: Respaldo; readonly modo: 'adjuntar' | 'reemplazar' };
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

const AdjuntarComprobanteDelAporte: React.FC<AdjuntarComprobanteDelAporteProps> = ({
  movimiento, entidad, paraAdjuntar, onClose, onSuccess,
}) => (
  <AdjuntarComprobanteModal
    isOpen
    entidad={entidad}
    modo={paraAdjuntar.modo}
    items={[{
      movimientoId: movimiento.id,
      respaldo: paraAdjuntar.respaldo,
      descripcion: movimiento.descripcion ?? null,
      monto: Number(movimiento.monto),
      fecha: paraAdjuntar.respaldo === 'reembolso' ? movimiento.reembolso_fecha ?? movimiento.fecha : movimiento.fecha,
    }]}
    onClose={onClose}
    onSuccess={onSuccess}
  />
);

export const EditarAporteModal: React.FC<EditarAporteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  movimiento,
  level = 'base',
}) => {
  const esModoMiembro = movimiento.origen_tipo === 'miembro';

  // Estado del formulario
  const [monto, setMonto] = useState(String(movimiento.monto));
  const [fecha, setFecha] = useState(movimiento.fecha);
  const [descripcion, setDescripcion] = useState(movimiento.descripcion ?? '');
  const [categoriaAporte, setCategoriaAporte] = useState<CategoriaAporte>(
    movimiento.categoria_aporte ?? 'aporte_mensual'
  );
  const [nombreExterno, setNombreExterno] = useState(movimiento.nombre_externo ?? '');
  const [nombreMiembro, setNombreMiembro] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [conComprobantes, setConComprobantes] = useState<{ readonly movimiento: Movimiento; readonly ahora: Date } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [paraAdjuntar, setParaAdjuntar] = useState<{ readonly respaldo: Respaldo; readonly modo: 'adjuntar' | 'reemplazar' } | null>(null);

  const { error, clearError, handleError } = useErrorHandler();

  // Editar no exige el comprobante: solo se puede cambiar el medio de pago y el folio, y adjuntar o
  // reemplazar el comprobante desde su propia ventana.
  const entidad: EntidadDinero | null = movimiento.lumisial_id
    ? { tipo: 'lumisial', id: movimiento.lumisial_id, nombre: '' }
    : null;
  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: true,
    sentidoDinero: 'ingreso',
    obligatoriedad: 'nunca',
    movimientoId: movimiento.id,
    medioPagoInicial: movimiento.medio_pago ?? null,
    folioInicial: movimiento.folio_recibo ?? null,
  });

  const { reiniciar: reiniciarComprobante, hayCambios: comprobanteConCambios } = comprobante;

  // Detectar cambios sin guardar
  const hasUnsavedChanges = useMemo(() => {
    if (Number(monto) !== movimiento.monto) return true;
    if (fecha !== movimiento.fecha) return true;
    if (descripcion !== (movimiento.descripcion ?? '')) return true;
    if (esModoMiembro && categoriaAporte !== (movimiento.categoria_aporte ?? 'aporte_mensual')) return true;
    if (!esModoMiembro && nombreExterno !== (movimiento.nombre_externo ?? '')) return true;
    return comprobanteConCambios;
  }, [monto, fecha, descripcion, categoriaAporte, nombreExterno, movimiento, esModoMiembro, comprobanteConCambios]);

  // Cargar nombre del miembro al abrir
  useEffect(() => {
    if (!isOpen || !esModoMiembro || !movimiento.origen_id) return;

    miembroService.getById(movimiento.origen_id).then((miembro) => {
      if (miembro) {
        setNombreMiembro(`${miembro.nombres} ${miembro.apellidos}`);
      }
    }).catch(() => {
      setNombreMiembro('');
    });
  }, [isOpen, esModoMiembro, movimiento.origen_id]);

  // El movimiento que llega de la lista puede no traer los resúmenes de sus comprobantes: se leen aquí.
  useEffect(() => {
    if (!isOpen) return;
    let vigente = true;
    movimientoService.getConComprobantes(movimiento.id)
      .then((leido) => { if (vigente && leido) setConComprobantes({ movimiento: leido, ahora: new Date() }); })
      .catch((e: unknown) => { if (vigente) reportarErrorInesperado(e, 'No pudimos cargar el comprobante del aporte.'); });
    return () => { vigente = false; };
  }, [isOpen, movimiento.id, recarga]);

  // Sincronizar estado cuando cambia el movimiento (ej. se abre con otro registro)
  useEffect(() => {
    if (!isOpen) return;
    setMonto(String(movimiento.monto));
    setFecha(movimiento.fecha);
    setDescripcion(movimiento.descripcion ?? '');
    setCategoriaAporte(movimiento.categoria_aporte ?? 'aporte_mensual');
    setNombreExterno(movimiento.nombre_externo ?? '');
    reiniciarComprobante();
    clearError();
  }, [isOpen, movimiento, clearError, reiniciarComprobante]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const montoNum = Number.parseFloat(monto);

    if (!monto || montoNum <= 0) {
      handleError('Por favor ingresa un monto válido mayor a 0');
      return;
    }

    if (!fecha) {
      handleError('La fecha es requerida');
      return;
    }

    if (!esModoMiembro && !nombreExterno.trim()) {
      handleError('El nombre del aportante externo es requerido');
      return;
    }

    const errorComprobante = comprobante.validar();
    if (errorComprobante) {
      handleError(errorComprobante);
      return;
    }

    try {
      setSubmitting(true);

      const { medio_pago, folio_recibo } = await comprobante.resolver();
      const payload: Partial<Omit<Movimiento, 'id' | 'created_at'>> = {
        monto: montoNum,
        fecha,
        descripcion: descripcion.trim() || undefined,
        medio_pago,
        folio_recibo,
      };

      if (esModoMiembro) {
        payload.categoria_aporte = categoriaAporte;
        payload.afecta_balance = categoriaAporte === 'aporte_mensual';
      } else {
        payload.nombre_externo = nombreExterno.trim();
      }

      await movimientoService.update(movimiento.id, payload);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al guardar los cambios. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  const titulo = esModoMiembro ? 'Editar Aporte' : 'Editar Aporte Externo';

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      level={level}
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Pencil className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">{titulo}</h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error inline */}
          <FormErrorAlert error={error} onClear={clearError} />

          <ComprobanteDelAporte
            conComprobantes={conComprobantes}
            puedeAdjuntar={entidad !== null}
            onAdjuntar={(respaldo, modo) => { setParaAdjuntar({ respaldo, modo }); }}
          />

          <DatosDelAportante
            esModoMiembro={esModoMiembro}
            nombreMiembro={nombreMiembro}
            nombreExterno={nombreExterno}
            onNombreExternoChange={setNombreExterno}
            categoria={categoriaAporte}
            onCategoriaChange={setCategoriaAporte}
          />

          {/* Monto y Fecha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label="Monto"
              type="number"
              value={monto}
              onChange={setMonto}
              placeholder="0"
              prefixIcon={<span className="text-slate-500 font-medium">$</span>}
              required
            />

            <TextInput
              label="Fecha"
              type="date"
              value={fecha}
              onChange={setFecha}
              required
            />
          </div>

          <MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />

          {/* Descripción */}
          <Textarea
            label="Descripción (opcional)"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Agrega detalles sobre este aporte..."
            rows={3}
          />

          {/* Acciones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
        {paraAdjuntar && entidad && (
          <AdjuntarComprobanteDelAporte
            movimiento={movimiento}
            entidad={entidad}
            paraAdjuntar={paraAdjuntar}
            onClose={() => { setParaAdjuntar(null); }}
            onSuccess={() => { setRecarga((n) => n + 1); onSuccess(); }}
          />
        )}
    </DialogShell>
  );
};
